import { useMemo } from 'react';
import { useCart } from '../context/CartContext';
import { useMeta } from '../lib/meta';
import { computeBundle, DEFAULT_TIERS, pct, type BundleResult } from '../lib/pricing';
import { formatCents } from '../lib/api';
import type { BundleTier } from '../types';

export function useBundle(): { result: BundleResult; tiers: BundleTier[] } {
  const cart = useCart();
  const meta = useMeta();
  const tiers = meta?.bundle_tiers?.length ? meta.bundle_tiers : DEFAULT_TIERS;
  const result = useMemo(
    () => computeBundle(cart.lines.map((l) => l.price_cents), tiers),
    [cart.lines, tiers],
  );
  return { result, tiers };
}

export function BundleProgress({ result, tiers, compact = false }: { result: BundleResult; tiers: BundleTier[]; compact?: boolean }) {
  const steps = tiers.filter((t) => t.rate > 0);
  if (result.count === 0) return null;

  return (
    <div className={compact ? '' : 'rounded-2xl border border-plug/20 bg-gradient-to-br from-plug/[0.04] to-transparent p-5'}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-[13px] font-semibold text-ink">
          {result.next ? (
            <>
              Add <span className="text-plug">{result.next.items_away} more</span>{' '}
              {result.next.items_away === 1 ? 'item' : 'items'} for{' '}
              <span className="text-plug">{pct(result.next.rate)} off</span> your whole cart
            </>
          ) : result.rate > 0 ? (
            <><span className="text-plug">{pct(result.rate)} bundle savings</span> unlocked. Your whole cart just got cheaper.</>
          ) : (
            <>Add 2+ pieces to unlock bundle savings.</>
          )}
        </p>
        {result.rate > 0 && (
          <span key={result.rate} className="inline-flex items-center gap-1 rounded-full bg-plug px-3 py-1 text-[12px] font-bold text-white animate-scale-in shrink-0">
            <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden><path d="M3 8.5l3 3 7-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            {pct(result.rate)} off
          </span>
        )}
      </div>
      <div className="mt-4 flex gap-1.5">
        {steps.map((t) => {
          const unlocked = result.count >= t.min;
          return (
            <div key={t.min} className="flex-1">
              <div className={`h-2 rounded-full transition-all duration-500 ${unlocked ? 'bg-plug shadow-sm' : 'bg-clay/60'}`} />
              <p className={`mt-1.5 text-center text-[10px] font-bold ${unlocked ? 'text-plug' : 'text-stone'}`}>
                {t.min}+ · {pct(t.rate)}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function SavingsRows({ result, currency = 'CAD' }: { result: BundleResult; currency?: string }) {
  return (
    <dl className="space-y-2 text-[14px]">
      <Row label="Subtotal" value={formatCents(result.subtotal_cents, currency)} />
      {result.discount_cents > 0 && (
        <div className="flex items-baseline justify-between text-plug">
          <dt className="font-medium">Bundle savings ({pct(result.rate)})</dt>
          <dd key={result.discount_cents} className="font-bold tabular animate-fade-in-up">
            −{formatCents(result.discount_cents, currency)}
          </dd>
        </div>
      )}
      <Row label="Local delivery" value={<span className="text-plug font-bold">FREE</span>} />
      <div className="mt-3 flex items-end justify-between border-t border-clay/60 pt-3">
        <span className="text-[12px] font-bold uppercase tracking-[0.08em] text-stone">Total</span>
        <span key={result.total_cents} className="font-display text-2xl font-extrabold text-ink tabular animate-fade-in-up">
          {formatCents(result.total_cents, currency)}
        </span>
      </div>
    </dl>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between">
      <dt className="text-stone">{label}</dt>
      <dd className="text-ink tabular font-medium">{value}</dd>
    </div>
  );
}
